import { Button } from '@davidlloydleisure/common-js-frontend';
import classNames from 'classnames';
import React, { forwardRef, use } from 'react';

import { isAbbyChatLink } from 'src/components/common/abby-chat/abby-chat-link';
import { WebsiteLink } from 'src/components/common/website-link';
import { WebsiteLinkButtonContent } from 'src/components/common/website-link-button-content';
import { SiteContext } from 'src/context/site-context';
import type { RedesignButtonStyle } from 'src/enums/button-style';
import abby from 'src/helpers/abby';
import { getBrandForBuildType } from 'src/helpers/brand-for-build-type';
import { buttonStyles } from 'src/helpers/button-styles';
import type { StaticTextLookupKey } from 'src/translators/static-text-lookup';
import { useStaticTextTranslator } from 'src/translators/static-text-translator';

import { getRedesignButtonStyleClasses } from './buttonCss';

type GlobalLinkButtonContentProps = {
  buttonText?: string | null;
  buttonKey?: StaticTextLookupKey;
  buttonLink?: string | null;
  buttonClassName?: string;
  buttonEndIcon?: React.ReactElement;
  buttonType?: string;
  gtmTrackingId?: string;
  className?: string;
  contentClassName?: string;
  buttonStyle?: Queries.ButtonStyle | null;
  isRedesignButton?: boolean;
  openInNewTab?: boolean;
  forceOpenInSameTab?: boolean;
  handleClick?: (event?: React.MouseEvent<HTMLElement>) => void;
};

const GlobalLinkButtonContent = forwardRef<HTMLElement, GlobalLinkButtonContentProps>(
  (
    {
      buttonText,
      buttonKey,
      buttonLink,
      buttonClassName,
      buttonEndIcon,
      buttonType,
      gtmTrackingId,
      className = '',
      contentClassName,
      buttonStyle,
      isRedesignButton,
      openInNewTab,
      forceOpenInSameTab,
      handleClick,
    },
    ref
  ) => {
    const translate = useStaticTextTranslator();
    const { buildType } = use(SiteContext);
    const tabIndexProp = isRedesignButton ? { tabIndex: 0 } : { tabIndex: 0 };
    const brand = getBrandForBuildType(buildType);
    const { main, wrapper } = getRedesignButtonStyleClasses(buttonStyle, buildType);

    const content = (
      <WebsiteLinkButtonContent
        buttonClassName={classNames(main, buttonClassName)}
        buttonKey={buttonKey}
        buttonText={buttonText}
        buttonEndIcon={buttonEndIcon}
        buttonType={buttonType}
        contentClassName={contentClassName}
        gtmTrackingId={gtmTrackingId}
      />
    );

    if (isAbbyChatLink(buttonLink)) {
      return (
        <Button
          brand={brand}
          onClick={() => {
            abby.open();
            handleClick?.();
          }}
          text={buttonText ? buttonText : translate(buttonKey!)}
          style={buttonStyles(buttonStyle as RedesignButtonStyle)}
          focusBorderClassName={classNames(className, 'abby-chat-link')}
          endIcon={buttonEndIcon}
          className={className}
        />
      );
    }

    const linkClassNames = classNames(isRedesignButton && wrapper, className);

    if (
      buttonLink &&
      (buttonLink.includes('http') || buttonLink.includes('localhost') || openInNewTab)
    ) {
      return (
        <a
          target={forceOpenInSameTab ? '_self' : '_blank'}
          rel={forceOpenInSameTab ? undefined : 'noopener noreferrer'}
          href={buttonLink}
          ref={ref as React.ForwardedRef<HTMLAnchorElement>}
          className={linkClassNames}
          onClick={(event) => handleClick?.(event)}
          {...tabIndexProp}
        >
          {content}
        </a>
      );
    }

    return (
      <WebsiteLink
        to={buttonLink || '/'}
        goToGlobal={true}
        className={linkClassNames}
        isTabIndex={isRedesignButton}
        forceOpenInSameTab={forceOpenInSameTab}
        handleClick={handleClick}
        ref={ref}
      >
        {content}
      </WebsiteLink>
    );
  }
);

GlobalLinkButtonContent.displayName = 'GlobalLinkButtonContent';

export default GlobalLinkButtonContent;
